import React, { useCallback, useEffect, useState } from 'react';
import { api, mutate } from '../shared/api';
import { readUrlParamNum, useUrlSync, CopyLinkButton } from '../shared/urlState';

type Locale = 'hr' | 'en';

type Warehouse = {
    id: number;
    code: string;
    name: string;
    is_default_visible: boolean | 0 | 1;
    include_in_total: boolean | 0 | 1;
    is_hub: boolean | 0 | 1;
    is_dispatch_warehouse: boolean | 0 | 1;
    ownership_type: string;
    /** Uloga u modulu 40; null = poslovnica (Vedran 26.09.2026). */
    coverage_role?: string | null;
    /** Kanta u informativnom bloku 40.6 (zaliha izvan pokrivenosti); null = nije izdvojeno. */
    info_bucket?: string | null;
    region: string | null;
    allowed_category_slugs: string | null;
    max_alloc_fine_g: number | null;
    notes: string | null;
    created_at: string;
    updated_at: string;
    stock_summary: {
        item_codes: number; qty_total: number; fine_g: number; cost_eur: number;
        gold?: { qty: number; fine_g: number; cost_eur: number };
        silver?: { qty: number; fine_g: number; cost_eur: number };
    } | null;
};

const OWNERSHIP_LABELS: Record<string, { hr: string; color: string }> = {
    own: { hr: 'OWN (naše)', color: '#1f7a36' },
    bugarska: { hr: 'BUGARSKA (sub.)', color: '#1e40af' },
    garancija: { hr: 'GARANCIJA (Argor)', color: '#1e40af' },
    tech: { hr: 'TECH (sistem)', color: 'var(--ad-ink)' },
    komisija: { hr: 'KOMISIJA (tuđe)', color: '#b14040' },
    pozajmica: { hr: 'POZAJMICA (dali)', color: '#b14040' },
    partner: { hr: 'PARTNER (tuđe)', color: '#b14040' },
    klijent: { hr: 'KLIJENT (štednja)', color: '#b14040' },
};

// Uloga u modulu 40 (40.6, 40.61, 40.62, 40.8, PG). Zamjenjuje sifre 064/211/065 u kodu.
const COVERAGE_ROLE_LABELS: Record<string, string> = {
    poslovnica: 'Poslovnica',
    veleprodaja: 'Veleprodaja',
    akcijsko: 'Akcijsko',
    samo_isporuke: 'Samo za isporuke',
    nadopuna_lagera: 'Nadopuna lagera',
};

// Kanta u informativnom bloku 40.6 (audit 40.6x N13). Zamjenjuje sifre 071/072/067/061/210/066 i nazive u kodu.
const INFO_BUCKET_LABELS: Record<string, string> = {
    '': '40.6: -',
    bugarska: '40.6: Bugarska',
    slovenija: '40.6: Slovenija',
    garancija: '40.6: Garancija',
    pomocna: '40.6: Pomoćna',
    komisija: '40.6: Komisija',
    zajam: '40.6: Zajam',
};

type WhSortKey = 'code' | 'name' | 'ownership_type' | 'region' | 'is_hub' | 'is_dispatch_warehouse' | 'is_default_visible' | 'include_in_total' | 'fine_g';

export function AdminGoldWarehousesSection({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [rows, setRows] = useState<Warehouse[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [drawerId, setDrawerId] = useState<number | null>(() => readUrlParamNum('wh'));
    useUrlSync('wh', drawerId == null ? null : String(drawerId), drawerId == null);
    const [drawerData, setDrawerData] = useState<any>(null);
    const [drawerLoading, setDrawerLoading] = useState(false);
    const [sortKey, setSortKey] = useState<WhSortKey>('code');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
    const toggleSort = (k: WhSortKey) => {
        if (sortKey === k) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        else { setSortKey(k); setSortDir('asc'); }
    };
    const sortArrow = (k: WhSortKey) => sortKey === k ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

    // ZBROJ svih skladišta (samo include_in_total) — klijentski, uvijek svjež nakon toggle-a.
    const totals = React.useMemo(() => {
        const acc = { n: 0, excluded: 0, qty: 0, goldFine: 0, goldCost: 0, silverFine: 0, silverCost: 0 };
        rows.forEach((r) => {
            if (!r.include_in_total) { acc.excluded++; return; }
            if (!r.stock_summary) return;
            acc.n++;
            acc.qty += r.stock_summary.qty_total;
            acc.goldFine += r.stock_summary.gold?.fine_g ?? 0;
            acc.goldCost += r.stock_summary.gold?.cost_eur ?? 0;
            acc.silverFine += r.stock_summary.silver?.fine_g ?? 0;
            acc.silverCost += r.stock_summary.silver?.cost_eur ?? 0;
        });
        return acc;
    }, [rows]);

    const sortedRows = React.useMemo(() => {
        const mul = sortDir === 'asc' ? 1 : -1;
        return [...rows].sort((a, b) => {
            const av: any = sortKey === 'fine_g' ? (a.stock_summary?.fine_g ?? 0) : (a as any)[sortKey];
            const bv: any = sortKey === 'fine_g' ? (b.stock_summary?.fine_g ?? 0) : (b as any)[sortKey];
            if (typeof av === 'number' && typeof bv === 'number') return mul * (av - bv);
            if (typeof av === 'boolean' && typeof bv === 'boolean') return mul * (Number(av) - Number(bv));
            return mul * String(av ?? '').localeCompare(String(bv ?? ''));
        });
    }, [rows, sortKey, sortDir]);

    const [spotG, setSpotG] = useState<number | null>(null);
    const [spotDate, setSpotDate] = useState<string | null>(null);
    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const r = await api<{ rows: Warehouse[]; spot_eur_per_g?: number | null; spot_date?: string | null }>('/admin/gold-warehouses', token);
            setRows(r.rows);
            setSpotG(r.spot_eur_per_g ?? null);
            setSpotDate(r.spot_date ?? null);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { void load(); }, [load]);

    // Ako URL ima ?wh=X i drawer još nije load-an, automatski otvori
    useEffect(() => {
        if (drawerId != null && !drawerData) {
            void openDrawer(drawerId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [saveToast, setSaveToast] = React.useState<{ id: number; field: string; status: 'saving' | 'ok' | 'err'; msg?: string } | null>(null);

    async function patchField(id: number, field: string, value: any) {
        // is_hub / is_dispatch_warehouse / is_default_visible — Laravel boolean validator → šaljemo bool
        const isBoolField = field === 'is_hub' || field === 'is_dispatch_warehouse' || field === 'is_default_visible' || field === 'include_in_total';
        const sentValue = isBoolField ? Boolean(value) : value;

        // OPTIMISTIC UPDATE — odmah patch-aj lokalno state da UI ne zaostaje
        setRows((prev) => prev.map((r) => r.id === id ? { ...r, [field]: sentValue } as Warehouse : r));
        setSaveToast({ id, field, status: 'saving', msg: `${field} = ${JSON.stringify(sentValue)}` });

        try {
            const body: Record<string, unknown> = { [field]: sentValue };
            console.log('[m.128 patch] sending', id, body);
            const r = await mutate<{ ok: boolean; affected: number; after: any }>(`/admin/gold-warehouses/${id}`, token, 'PATCH', body);
            console.log('[m.128 patch] response', r);
            // Coerce bool fields jer MySQL TINYINT vraća string "0"/"1" — `!!"0"` je true (gotcha)
            if (r.after) {
                ['is_hub', 'is_dispatch_warehouse', 'is_default_visible', 'include_in_total'].forEach((k) => {
                    const v = r.after[k];
                    if (v !== undefined && typeof v !== 'boolean') {
                        r.after[k] = v === 1 || v === '1' || v === true;
                    }
                });
            }
            const serverValue = r.after?.[field];
            const matched = serverValue === sentValue || (typeof serverValue === 'boolean' && typeof sentValue === 'boolean' && serverValue === sentValue);
            setSaveToast({
                id, field,
                status: matched ? 'ok' : 'err',
                msg: matched
                    ? `${field} = ${JSON.stringify(sentValue)} (server: ${JSON.stringify(serverValue)}, affected=${r.affected})`
                    : `MISMATCH: poslano ${JSON.stringify(sentValue)} ali server vraća ${JSON.stringify(serverValue)} (affected=${r.affected})`,
            });
            setTimeout(() => setSaveToast((s) => s && s.id === id && s.field === field ? null : s), 3000);
            // Patch sa svježim server vrijednostima umjesto da reloadamo cijelu listu (izbjegava cache trouble)
            setRows((prev) => prev.map((row) => row.id === id && r.after ? { ...row, ...r.after } as Warehouse : row));
        } catch (e) {
            const msg = e instanceof Error ? e.message : 'API';
            console.error('[m.128 patch] ERR', e);
            setSaveToast({ id, field, status: 'err', msg: `${field} = ${JSON.stringify(sentValue)} → ${msg}` });
            setError(`${msg} (field=${field} value=${JSON.stringify(sentValue)})`);
            // Revert optimistic update on failure
            await load();
        }
    }

    async function openDrawer(id: number) {
        setDrawerId(id);
        setDrawerLoading(true);
        try {
            const r = await api<any>(`/admin/gold-warehouses/${id}/stock`, token);
            setDrawerData(r);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        } finally {
            setDrawerLoading(false);
        }
    }

    const fmt = (n: number, dp = 0) => Number(n).toLocaleString(locale === 'en' ? 'en-GB' : 'hr-HR', { minimumFractionDigits: dp, maximumFractionDigits: dp });

    return (
        <section className="card admin-block">
            <div className="bi-module-hero">
                <div className="bi-module-hero-icon">🏪</div>
                <div className="bi-module-hero-text">
                    <h1>128 · {t('Skladišta zlata', 'Gold warehouses')}</h1>
                    <p className="muted">
                        {t(
                            'Master data: vlasništvo (own/komisija/partner...), HUB flag, dispatch-eligibility za DeliveryPlanEngine. Klik na red → drawer sa stanjem.',
                            'Master data: ownership (own/komisija/partner...), HUB flag, dispatch-eligibility for DeliveryPlanEngine. Click row → drawer with stock.',
                        )}
                    </p>
                </div>
                <CopyLinkButton locale={locale} />
            </div>

            {error ? <p className="error">{error}</p> : null}
            {loading ? <p className="muted">{t('Učitavam…', 'Loading…')}</p> : null}
            {saveToast ? (
                <div style={{
                    position: 'fixed', top: 70, right: 20, zIndex: 9999,
                    padding: '8px 14px', borderRadius: 6, fontWeight: 700, fontSize: '0.84rem',
                    background: saveToast.status === 'ok' ? '#1f7a36' : saveToast.status === 'err' ? '#b14040' : 'var(--ad-acc-3)',
                    color: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                }}>
                    {saveToast.status === 'saving' ? '⟳ Spremam…' : saveToast.status === 'ok' ? `✓ Spremljeno: ${saveToast.msg}` : `✗ Greška: ${saveToast.msg}`}
                </div>
            ) : null}

            <datalist id="bi-wh-regions">
                {Array.from(new Set(rows.map((r) => r.region).filter(Boolean))).map((reg) => (
                    <option key={reg ?? ''} value={reg ?? ''} />
                ))}
            </datalist>

            {/* ZBROJ svih skladišta (checkbox "U zbroju" odlučuje što ulazi) */}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'baseline', border: '1px solid #e6d8b0', background: '#fbf7ee', borderRadius: 10, padding: '10px 14px', margin: '10px 0' }}>
                <span style={{ fontWeight: 700, color: '#6b4f1c' }}>Σ {t('ZBROJ', 'TOTAL')} ({totals.n} {t('skladišta', 'warehouses')}{totals.excluded > 0 ? `, ${totals.excluded} ${t('izuzeto', 'excluded')}` : ''})</span>
                <span>🥇 {t('Zlato', 'Gold')}: <strong>{new Intl.NumberFormat('hr-HR', { maximumFractionDigits: 0 }).format(totals.goldFine)} g</strong> · {t('nabavna', 'cost')} {new Intl.NumberFormat('hr-HR', { maximumFractionDigits: 0 }).format(totals.goldCost)} €{spotG != null ? <> · <span style={{ color: '#1f7a36' }}>{t('spot', 'spot')} <strong>{new Intl.NumberFormat('hr-HR', { maximumFractionDigits: 0 }).format(totals.goldFine * spotG)} €</strong></span></> : null}</span>
                <span>🥈 {t('Srebro', 'Silver')}: <strong>{new Intl.NumberFormat('hr-HR', { maximumFractionDigits: 0 }).format(totals.silverFine)} g</strong> · {new Intl.NumberFormat('hr-HR', { maximumFractionDigits: 0 }).format(totals.silverCost)} €</span>
                <span>{t('Ukupno kom', 'Total qty')}: <strong>{new Intl.NumberFormat('hr-HR', { maximumFractionDigits: 0 }).format(totals.qty)}</strong></span>
                <span style={{ fontWeight: 700 }}>{t('Vrijednost (nabavna)', 'Value (cost)')}: <strong>{new Intl.NumberFormat('hr-HR', { maximumFractionDigits: 0 }).format(totals.goldCost + totals.silverCost)} €</strong></span>
                {spotG != null ? <span style={{ fontWeight: 700, color: '#1f7a36' }}>{t('Vrijednost (spot', 'Value (spot')} {spotDate ? spotDate.slice(8, 10) + '.' + spotDate.slice(5, 7) + '.' : ''}, {new Intl.NumberFormat('hr-HR', { maximumFractionDigits: 1 }).format(spotG)} €/g): <strong>{new Intl.NumberFormat('hr-HR', { maximumFractionDigits: 0 }).format(totals.goldFine * spotG + totals.silverCost)} €</strong></span> : null}
            </div>

            <div className="table-wrap">
                <table className="admin-user-table" style={{ fontSize: '0.82rem' }}>
                    <thead>
                        <tr>
                            <th onClick={() => toggleSort('code')} style={{ cursor: 'pointer', userSelect: 'none' }}>{t('Šifra', 'Code')}{sortArrow('code')}</th>
                            <th onClick={() => toggleSort('name')} style={{ cursor: 'pointer', userSelect: 'none' }}>{t('Naziv', 'Name')}{sortArrow('name')}</th>
                            <th onClick={() => toggleSort('ownership_type')} style={{ cursor: 'pointer', userSelect: 'none' }}>{t('Vlasništvo', 'Ownership')}{sortArrow('ownership_type')}</th>
                            <th title={t('Uloga u modulu 40: veleprodaja i akcijsko nisu poslovnice; skladište samo za isporuke cijelu zalihu daje u smješteno; nadopuna lagera je cijela u L1 i nije stupac plana 40.8', 'Role in module 40')}>{t('Uloga (40)', 'Role (40)')}</th>
                            <th onClick={() => toggleSort('region')} style={{ cursor: 'pointer', userSelect: 'none' }}>{t('Region', 'Region')}{sortArrow('region')}</th>
                            <th onClick={() => toggleSort('is_hub')} style={{ textAlign: 'center', cursor: 'pointer', userSelect: 'none' }} title={t('Hub skladište — služi kao distribucijski centar (L1 sloj)', 'Hub warehouse')}>HUB{sortArrow('is_hub')}</th>
                            <th onClick={() => toggleSort('is_dispatch_warehouse')} style={{ textAlign: 'center', cursor: 'pointer', userSelect: 'none' }} title={t('Skladište ulazi u DeliveryPlanEngine stock pool', 'In DeliveryPlanEngine stock pool')}>{t('Dispatch', 'Dispatch')}{sortArrow('is_dispatch_warehouse')}</th>
                            <th onClick={() => toggleSort('is_default_visible')} style={{ textAlign: 'center', cursor: 'pointer', userSelect: 'none' }} title={t('Vidljivo po defaultu u Planeru', 'Visible by default')}>{t('Vidljivo', 'Visible')}{sortArrow('is_default_visible')}</th>
                            <th onClick={() => toggleSort('include_in_total')} style={{ textAlign: 'center', cursor: 'pointer', userSelect: 'none' }} title={t('Ulazi u ZBROJ svih skladišta (traka iznad tablice)', 'Included in the all-warehouses TOTAL')}>{t('U zbroju', 'In total')}{sortArrow('include_in_total')}</th>
                            <th onClick={() => toggleSort('fine_g')} style={{ textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}>{t('Stanje', 'Stock')}{sortArrow('fine_g')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedRows.map((r) => {
                            const o = OWNERSHIP_LABELS[r.ownership_type] ?? { hr: r.ownership_type || '—', color: '#666' };
                            const inNet = ['own', 'bugarska', 'garancija', 'tech'].includes(r.ownership_type);
                            return (
                                <tr key={r.id} onClick={() => void openDrawer(r.id)} style={{ cursor: 'pointer', background: !inNet ? 'rgba(177,64,64,0.04)' : undefined }} title={t('Klik za stock detalje', 'Click for stock details')}>
                                    <td><strong>{r.code}</strong></td>
                                    <td>{r.name}</td>
                                    <td onClick={(e) => e.stopPropagation()}>
                                        <select value={r.ownership_type} onChange={(e) => void patchField(r.id, 'ownership_type', e.target.value)} style={{ fontSize: '0.74rem', padding: '2px 4px', color: o.color, fontWeight: 700, border: '1px solid #ddd', borderRadius: 3 }}>
                                            {Object.entries(OWNERSHIP_LABELS).map(([k, l]) => <option key={k} value={k}>{l.hr}</option>)}
                                        </select>
                                        {!inNet ? <div style={{ fontSize: '0.66rem', color: '#b14040', marginTop: 2 }}>⚠ {t('Ne ulazi u 40.5 Net Position', 'Excluded from 40.5 Net Position')}</div> : null}
                                    </td>
                                    <td onClick={(e) => e.stopPropagation()}>
                                        <select value={r.coverage_role ?? 'poslovnica'} onChange={(e) => void patchField(r.id, 'coverage_role', e.target.value === 'poslovnica' ? null : e.target.value)} style={{ fontSize: '0.74rem', padding: '2px 4px', border: '1px solid #ddd', borderRadius: 3 }}>
                                            {Object.entries(COVERAGE_ROLE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                                        </select>
                                        <select value={r.info_bucket ?? ''} onChange={(e) => void patchField(r.id, 'info_bucket', e.target.value === '' ? null : e.target.value)} title={t('Kanta u 40.6: zaliha izvan pokrivenosti', 'Bucket in 40.6: stock outside coverage')} style={{ fontSize: '0.74rem', padding: '2px 4px', border: '1px solid #ddd', borderRadius: 3, marginTop: 2, display: 'block' }}>
                                            {Object.entries(INFO_BUCKET_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                                        </select>
                                    </td>
                                    <td onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="text"
                                            list="bi-wh-regions"
                                            defaultValue={r.region ?? ''}
                                            placeholder="Centar / Sjever / Istok / Jug"
                                            onClick={(e) => e.stopPropagation()}
                                            onBlur={(e) => { const v = e.target.value.trim(); if (v !== (r.region ?? '')) void patchField(r.id, 'region', v || null); }}
                                            style={{ width: 90, padding: '2px 5px', fontSize: '0.74rem', border: '1px solid #ddd', borderRadius: 3 }}
                                        />
                                    </td>
                                    <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                                        <input type="checkbox" checked={!!r.is_hub} onClick={(e) => e.stopPropagation()} onChange={(e) => { e.stopPropagation(); void patchField(r.id, 'is_hub', e.target.checked ? 1 : 0); }} />
                                    </td>
                                    <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                                        <input type="checkbox" checked={!!r.is_dispatch_warehouse} onClick={(e) => e.stopPropagation()} onChange={(e) => { e.stopPropagation(); void patchField(r.id, 'is_dispatch_warehouse', e.target.checked ? 1 : 0); }} title={t('Koristi se za isporuke iz DeliveryPlanEngine', 'Used for dispatches in DeliveryPlanEngine')} />
                                    </td>
                                    <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                                        <input type="checkbox" checked={!!r.is_default_visible} onClick={(e) => e.stopPropagation()} onChange={(e) => { e.stopPropagation(); void patchField(r.id, 'is_default_visible', e.target.checked ? 1 : 0); }} />
                                    </td>
                                    <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                                        <input type="checkbox" checked={!!r.include_in_total} onClick={(e) => e.stopPropagation()} onChange={(e) => { e.stopPropagation(); void patchField(r.id, 'include_in_total', e.target.checked ? 1 : 0); }} title={t('Uključi/isključi iz zbroja svih skladišta', 'Include/exclude from total')} />
                                    </td>
                                    <td style={{ textAlign: 'right', fontSize: '0.74rem' }}>
                                        {r.stock_summary ? (
                                            <>
                                                {r.stock_summary.gold && r.stock_summary.gold.fine_g > 0 ? (
                                                    <div style={{ color: 'var(--ad-acc)' }}>
                                                        <strong>Au {fmt(r.stock_summary.gold.fine_g / 1000, 3)} kg</strong>
                                                    </div>
                                                ) : null}
                                                {r.stock_summary.silver && r.stock_summary.silver.fine_g > 0 ? (
                                                    <div style={{ color: '#7a7a7a' }}>
                                                        <strong>Ag {fmt(r.stock_summary.silver.fine_g / 1000, 3)} kg</strong>
                                                    </div>
                                                ) : null}
                                                <div className="muted" style={{ fontSize: '0.7rem' }}>
                                                    {r.stock_summary.qty_total} kom · {t('nab.', 'cost')} {fmt(r.stock_summary.cost_eur)} €
                                                </div>
                                                {spotG != null && r.stock_summary.gold && r.stock_summary.gold.fine_g > 0 ? (
                                                    <div style={{ fontSize: '0.7rem', color: '#1f7a36' }}>
                                                        {t('spot', 'spot')} {fmt(r.stock_summary.gold.fine_g * spotG + (r.stock_summary.silver?.cost_eur ?? 0))} €
                                                    </div>
                                                ) : null}
                                            </>
                                        ) : <span className="muted">—</span>}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Stock drawer */}
            {drawerId ? (
                <div role="dialog" aria-modal="true" onClick={() => { setDrawerId(null); setDrawerData(null); }}
                    style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}
                >
                    <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(820px, 95vw)', background: '#fff', height: '100vh', overflowY: 'auto', boxShadow: '-4px 0 16px rgba(0,0,0,0.2)' }}>
                        <div style={{ padding: '14px 22px', borderBottom: '1px solid #ddd4c1', position: 'sticky', top: 0, background: '#fff', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ margin: 0, color: 'var(--ad-ink)' }}>
                                🏪 {drawerData?.warehouse?.code} · {drawerData?.warehouse?.name}
                            </h3>
                            <button type="button" onClick={() => { setDrawerId(null); setDrawerData(null); }} className="secondary compact" style={{ fontSize: '0.84rem' }}>✕</button>
                        </div>
                        <div style={{ padding: '14px 22px' }}>
                            {drawerLoading ? <p className="muted">{t('Učitavam…', 'Loading…')}</p> : null}
                            {drawerData?.warehouse ? (
                                <>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 12 }}>
                                        <div style={{ background: '#fafaf5', padding: '8px 10px', borderRadius: 4 }}>
                                            <div style={{ fontSize: '0.7rem', color: 'rgba(0,0,0,0.5)' }}>{t('Vlasništvo', 'Ownership')}</div>
                                            <strong style={{ color: OWNERSHIP_LABELS[drawerData.warehouse.ownership_type]?.color ?? '#666' }}>{OWNERSHIP_LABELS[drawerData.warehouse.ownership_type]?.hr ?? drawerData.warehouse.ownership_type}</strong>
                                        </div>
                                        <div style={{ background: '#fafaf5', padding: '8px 10px', borderRadius: 4 }}>
                                            <div style={{ fontSize: '0.7rem', color: 'rgba(0,0,0,0.5)' }}>HUB / Dispatch</div>
                                            <strong>{drawerData.warehouse.is_hub ? '✓ HUB' : '—'} {drawerData.warehouse.is_dispatch_warehouse ? '· ✓ Dispatch' : '· ✗ Dispatch'}</strong>
                                        </div>
                                        <div style={{ background: '#fafaf5', padding: '8px 10px', borderRadius: 4 }}>
                                            <div style={{ fontSize: '0.7rem', color: 'rgba(0,0,0,0.5)' }}>{t('Region', 'Region')}</div>
                                            <strong>{drawerData.warehouse.region ?? '—'}</strong>
                                        </div>
                                    </div>
                                    {drawerData.total ? (
                                        <div style={{ background: 'rgba(var(--ad-acc-rgb),0.08)', padding: '10px 14px', borderRadius: 6, marginBottom: 12, fontSize: '0.86rem' }}>
                                            <div style={{ marginBottom: 4 }}>
                                                <strong>Σ {t('Stanje', 'Stock')}</strong>:
                                                · {drawerData.total.item_codes} {t('vrsta', 'sku')}
                                                · {drawerData.total.qty_total} {t('kom', 'pcs')}
                                                · cost {fmt(drawerData.total.cost_eur)} €
                                            </div>
                                            {drawerData.total.gold && drawerData.total.gold.fine_g > 0 ? (
                                                <div style={{ color: 'var(--ad-acc)', marginTop: 2 }}>
                                                    🥇 <strong>Au: {fmt(drawerData.total.gold.fine_g / 1000, 3)} kg</strong> fine
                                                    · {drawerData.total.gold.qty} {t('kom', 'pcs')}
                                                    · cost {fmt(drawerData.total.gold.cost_eur)} €
                                                    · MTM {fmt(drawerData.total.gold.mtm_eur)} € @ spot {drawerData.spot?.eur_per_g} €/g
                                                </div>
                                            ) : null}
                                            {drawerData.total.silver && drawerData.total.silver.fine_g > 0 ? (
                                                <div style={{ color: '#7a7a7a', marginTop: 2 }}>
                                                    🥈 <strong>Ag: {fmt(drawerData.total.silver.fine_g / 1000, 3)} kg</strong> fine
                                                    · {drawerData.total.silver.qty} {t('kom', 'pcs')}
                                                    · cost {fmt(drawerData.total.silver.cost_eur)} €
                                                    · MTM ~{fmt(drawerData.total.silver.mtm_eur)} € @ ~{drawerData.spot?.silver_eur_per_g} €/g
                                                    {drawerData.spot?.silver_note ? <span className="muted" style={{ fontSize: '0.72rem', marginLeft: 6 }}>({drawerData.spot.silver_note})</span> : null}
                                                </div>
                                            ) : null}
                                        </div>
                                    ) : null}
                                    <h4 style={{ margin: '10px 0 6px', fontSize: '0.84rem' }}>{t('Top stavke po fine_g', 'Top items by fine_g')}</h4>
                                    <table className="admin-user-table" style={{ fontSize: '0.76rem' }}>
                                        <thead>
                                            <tr>
                                                <th>SKU</th>
                                                <th>{t('Naziv', 'Name')}</th>
                                                <th>{t('Metal', 'Metal')}</th>
                                                <th>{t('Kateg.', 'Cat.')}</th>
                                                <th>{t('Kovnica', 'Mint')}</th>
                                                <th style={{ textAlign: 'right' }} title={t('Stanje (stock + na putu)', 'Stock (incl. in transit)')}>{t('Stanje', 'Stock')}</th>
                                                <th style={{ textAlign: 'right', width: 50 }} title={t('Pending rezervacije iz m.40.3', 'Pending m.40.3 reservations')}>{t('Rez', 'Rsv')}</th>
                                                <th style={{ textAlign: 'right', width: 50 }} title={t('Raspoloživo (stanje − rezervirano)', 'Available (stock − reserved)')}>{t('Rasp', 'Avl')}</th>
                                                <th style={{ textAlign: 'right' }}>Fine g</th>
                                                <th style={{ textAlign: 'right' }}>Cost €</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(drawerData.items ?? []).map((it: any, i: number) => {
                                                const isSilver = (it.metal_type ?? 'gold') === 'silver';
                                                const reserved = Number(it.reserved_qty ?? 0);
                                                const available = Number(it.available_qty ?? Number(it.qty_stock) + Number(it.qty_in_transit ?? 0));
                                                const transit = Number(it.qty_in_transit ?? 0);
                                                // Stanje sad uključuje na putu (qty_stock + qty_in_transit)
                                                const stanje = Number(it.qty_stock) + transit;
                                                return (
                                                <tr key={i} style={{ background: isSilver ? 'rgba(122,122,122,0.05)' : undefined }}>
                                                    <td style={{ fontSize: '0.7rem' }}>{it.sku ?? it.item_code}</td>
                                                    <td>{(it.name_hr ?? '—').slice(0, 36)}</td>
                                                    <td style={{ fontSize: '0.7rem', color: isSilver ? '#7a7a7a' : 'var(--ad-acc)', fontWeight: 700 }}>{isSilver ? '🥈 Ag' : '🥇 Au'}</td>
                                                    <td style={{ fontSize: '0.7rem' }}>{it.cgm_category ?? '—'}</td>
                                                    <td style={{ fontSize: '0.7rem' }}>{it.cgm_mint ?? '—'}</td>
                                                    <td style={{ textAlign: 'right' }} title={transit > 0 ? t(`Stock ${it.qty_stock} + na putu ${transit}`, `Stock ${it.qty_stock} + in transit ${transit}`) : ''}>{stanje}{transit > 0 ? <span style={{ fontSize: '0.62rem', color: 'var(--ad-acc-3)', marginLeft: 2 }}>(+{transit})</span> : null}</td>
                                                    <td style={{ textAlign: 'right', color: reserved > 0 ? 'var(--ad-acc)' : 'rgba(0,0,0,0.3)', fontWeight: reserved > 0 ? 700 : 400 }}>{reserved > 0 ? reserved : '—'}</td>
                                                    <td style={{ textAlign: 'right', fontWeight: 700, color: available > 0 ? '#1f7a36' : '#b14040' }}>{available}</td>
                                                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(it.fine_total, 2)}</td>
                                                    <td style={{ textAlign: 'right' }}>{fmt(it.purchase_price_eur)} €</td>
                                                </tr>
                                                );
                                            })}
                                            {(!drawerData.items || drawerData.items.length === 0) ? (
                                                <tr><td colSpan={10} className="muted" style={{ textAlign: 'center' }}>{t('Nema stavki u zalihi.', 'No items in stock.')}</td></tr>
                                            ) : null}
                                        </tbody>
                                    </table>
                                </>
                            ) : null}
                        </div>
                    </div>
                </div>
            ) : null}
        </section>
    );
}
